import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import { PlusIcon } from '../icons/PlusIcon.jsx';
import { MinusIcon } from '../icons/MinusIcon.jsx';

import './passengers.css';

export function Passengers(props) {
    const indexPostfix = props.index >= 0 ? `-${props.index}` : '';

    function renderPassengerCountWarning() {
        return props.showPassengerWarning ? (
            <div className="grid u-ma16 u-mt32 u-cherry" data-testid="passenger-count-warning">
                {props.t('passengers.inputs.countWarning')}
            </div>
        ) : null;
    }
    renderPassengerCountWarning.propTypes = Passengers.propTypes;

    function renderChildAgesMobile(age, index) {
        const childIndexPostfix = props.index >= 0 ? `${indexPostfix}-${index}` : '';
        return (
            <div key={`child_${index}`} className="grid u-ma16 u-mt32">
                <div className="grid__unit u-size1of2 u-left u-mt8 u-truncate">
                    {props.t('passengers.inputs.childTitle')} {index + 1}
                </div>
                <div className="grid__unit u-size1of2 u-position-relative">
                    <button
                        type="button"
                        className="transparent u-float-left"
                        aria-label={props.t('passengers.inputs.decChildAge')}
                        onClick={() => props.decreaseChildAge(index)}
                        data-testid={`dec-child-age${childIndexPostfix}`}>
                        <MinusIcon width={48} height={48} className="u-mh8" />
                    </button>
                    <span className="centerAge u-t3" data-testid={`child-age${childIndexPostfix}`}>
                        {age} {props.t('passengers.inputs.agePostfix')}
                    </span>
                    <button
                        type="button"
                        aria-label={props.t('passengers.inputs.incChildAge')}
                        className="transparent u-float-right u-mr0"
                        onClick={() => props.increaseChildAge(index)}
                        data-testid={`inc-child-age${childIndexPostfix}`}>
                        <PlusIcon width={48} height={48} className="u-mh8" />
                    </button>
                </div>
            </div>
        );
    }
    renderChildAgesMobile.propTypes = Passengers.propTypes;

    function renderChildAgesDesktop(age, index) {
        const childIndexPostfix = index >= 0 ? `${indexPostfix}-${index}` : '';
        return (
            <div key={`child_${index}`} className="grid u-pa8 no-text-select">
                <div className="grid__unit u-size1of2 u-left u-pl2 u-b1 u-truncate">
                    {props.t('passengers.inputs.ageTitle')} {index + 1}
                </div>
                <div className="grid__unit u-size1of2 u-position-relative">
                    <button
                        type="button"
                        className="transparent u-float-left u-ml0"
                        onClick={() => props.decreaseChildAge(index)}
                        aria-label={props.t('passengers.inputs.decChildAge')}
                        tabIndex="-1"
                        data-testid={`dec-child-age${childIndexPostfix}`}>
                        <MinusIcon width={28} height={28} />
                    </button>

                    <button
                        type="button"
                        className="transparent u-float-right u-mr0"
                        onClick={() => props.increaseChildAge(index)}
                        tabIndex="-1"
                        aria-label={props.t('passengers.inputs.incChildAge')}
                        data-testid={`inc-child-age${childIndexPostfix}`}>
                        <PlusIcon width={28} height={28} />
                    </button>

                    <input
                        id={`child-age-input${childIndexPostfix}`}
                        type="number"
                        className="passenger-input"
                        data-testid={`child-age${childIndexPostfix}`}
                        onFocus={event => event.target.select()}
                        onChange={({ target: { value } }) => props.setChildAge({ index, value })}
                        value={age || 0}
                    />
                    <label htmlFor={`child-age-input${childIndexPostfix}`} className="u-screen-reader-only">
                        {props.t('passengers.inputs.ageTitle')} {index + 1}
                    </label>
                </div>
            </div>
        );
    }
    renderChildAgesDesktop.propTypes = Passengers.propTypes;

    function renderMobile() {
        return (
            <>
                <div className="grid u-ma16 u-mt24">
                    <div className={`grid__unit u-size1of2 u-left u-mt8 u-truncate${props.showPassengerWarning ? ' u-cherry' : ''}`}>
                        {props.t('passengers.inputs.adultLabel')}
                    </div>
                    <div className="grid__unit u-size1of2 u-position-relative">
                        <button
                            type="button"
                            className="transparent u-float-left"
                            onClick={props.removeAdult}
                            aria-label={props.t('passengers.inputs.removeAdult')}
                            data-testid={`remove-adult${indexPostfix}`}>
                            <MinusIcon id="decrease_adults" width={48} height={48} className="u-mh8" />
                        </button>
                        <span id="adults_count" data-testid={`adults-count${indexPostfix}`} className="centerAge u-t3">
                            {props.adults}
                        </span>
                        <button
                            type="button"
                            className="transparent u-float-right u-mr0"
                            onClick={props.addAdult}
                            aria-label={props.t('passengers.inputs.addAdult')}
                            disabled={props.showPassengerWarning}
                            data-testid={`add-adult${indexPostfix}`}>
                            <PlusIcon id="increase_adults" width={48} height={48} className="u-mh8" disabled={props.showPassengerWarning} />
                        </button>
                    </div>
                </div>
                <div className={`grid u-ma16 u-mt32 ${props.children.length === 0 ? '' : 'u-pb8'}`}>
                    <div className={`grid__unit u-size1of2 u-left u-mt8 u-truncate${props.showPassengerWarning ? ' u-cherry' : ''}`}>
                        {props.t('passengers.inputs.childLabel')}
                    </div>
                    <div className="grid__unit u-size1of2 u-position-relative">
                        <button
                            type="button"
                            className="transparent u-float-left"
                            onClick={props.removeChild}
                            aria-label={props.t('passengers.inputs.removeChild')}
                            data-testid={`remove-child${indexPostfix}`}>
                            <MinusIcon width={48} height={48} className="u-mh8" />
                        </button>
                        <span className="centerAge u-t3" data-testid={`children-count${indexPostfix}`}>
                            {props.children.length}
                        </span>
                        <button
                            type="button"
                            className="transparent u-float-right"
                            onClick={props.addChild}
                            aria-label={props.t('passengers.inputs.addChild')}
                            disabled={props.showPassengerWarning}
                            data-testid={`add-child${indexPostfix}`}>
                            <PlusIcon width={48} height={48} className="u-mh8" disabled={props.showPassengerWarning} />
                        </button>
                    </div>
                </div>
                {props.children.map(renderChildAgesMobile)}
                {renderPassengerCountWarning()}
            </>
        );
    }
    renderMobile.propTypes = Passengers.propTypes;

    function renderDesktop() {
        return (
            <>
                <div className="grid u-pa8 no-text-select">
                    <div className={`grid__unit u-size1of2 u-left u-pl2 u-b1 u-truncate${props.showPassengerWarning ? ' u-cherry' : ''}`}>
                        {props.t('passengers.inputs.adultLabel')}
                    </div>
                    <div className="grid__unit u-right u-size1of2 u-position-relative u-pt4">
                        <button
                            type="button"
                            className="transparent u-float-left u-ml0"
                            onClick={props.removeAdult}
                            aria-label={props.t('passengers.inputs.removeAdult')}
                            tabIndex="-1"
                            data-testid={`remove-adult${indexPostfix}`}>
                            <MinusIcon width={28} height={28} />
                        </button>

                        <button
                            type="button"
                            className="transparent u-float-right u-mr0"
                            onClick={props.addAdult}
                            aria-label={props.t('passengers.inputs.addAdult')}
                            tabIndex="-1"
                            disabled={props.showPassengerWarning}
                            data-testid={`add-adult${indexPostfix}`}>
                            <PlusIcon width={28} height={28} disabled={props.showPassengerWarning} />
                        </button>

                        <input
                            id={`passenger-input-adults${indexPostfix}`}
                            type="number"
                            className="passenger-input"
                            data-testid={`adults-count${indexPostfix}`}
                            autoFocus
                            onFocus={event => event.target.select()}
                            onChange={({ target: { value } }) => props.setAdults(value === '' ? 0 : value)}
                            value={props.adults || 1}
                        />
                        <label htmlFor={`passenger-input-adults${indexPostfix}`} className="u-screen-reader-only">
                            {props.t('passengers.inputs.adultLabel')}
                        </label>
                    </div>
                </div>
                <div className={`grid u-pa8 no-text-select ${props.children.length === 0 ? '' : 'u-pb16'}`}>
                    <div className={`grid__unit u-size1of2 u-left u-pl2 u-b1 u-truncate${props.showPassengerWarning ? ' u-cherry' : ''}`}>
                        {props.t('passengers.inputs.childLabel')}
                    </div>
                    <div className="grid__unit u-size1of2 u-position-relative u-pt4">
                        <button
                            type="button"
                            className="transparent u-float-left u-ml0"
                            onClick={props.removeChild}
                            aria-label={props.t('passengers.inputs.removeChild')}
                            tabIndex="-1"
                            data-testid={`remove-child${indexPostfix}`}>
                            <MinusIcon width={28} height={28} />
                        </button>

                        <button
                            type="button"
                            className="transparent u-float-right u-mr0"
                            onClick={props.addChild}
                            aria-label={props.t('passengers.inputs.addChild')}
                            tabIndex="-1"
                            disabled={props.showPassengerWarning}
                            data-testid={`add-child${indexPostfix}`}>
                            <PlusIcon width={28} height={28} disabled={props.showPassengerWarning} />
                        </button>

                        <input
                            id={`passenger-input-children${indexPostfix}`}
                            type="number"
                            className="passenger-input"
                            data-testid={`children-count${indexPostfix}`}
                            onChange={({ target: { value } }) => props.setChildren(value)}
                            onFocus={event => event.target.select()}
                            value={props.children.length}
                        />
                        <label htmlFor={`passenger-input-children${indexPostfix}`} className="u-screen-reader-only">
                            {props.t('passengers.inputs.childLabel')}
                        </label>
                    </div>
                </div>
                {props.children.map(renderChildAgesDesktop)}
                {renderPassengerCountWarning()}
            </>
        );
    }
    renderDesktop.propTypes = Passengers.propTypes;

    return props.isMobile ? renderMobile() : renderDesktop();
}

Passengers.propTypes = {
    t: PropTypes.func.isRequired,
    adults: PropTypes.number.isRequired,
    children: PropTypes.array.isRequired,
    addAdult: PropTypes.func.isRequired,
    removeAdult: PropTypes.func.isRequired,
    setAdults: PropTypes.func.isRequired,
    addChild: PropTypes.func.isRequired,
    removeChild: PropTypes.func.isRequired,
    setChildren: PropTypes.func.isRequired,
    increaseChildAge: PropTypes.func.isRequired,
    decreaseChildAge: PropTypes.func.isRequired,
    setChildAge: PropTypes.func.isRequired,
    index: PropTypes.number,
    showPassengerWarning: PropTypes.bool,
    isMobile: PropTypes.bool,
};

export default withTranslation('search')(Passengers);
